/** Line icons for node types, keyed by the `icon` name the catalogue sends. */

import type { SVGProps } from 'react'

const paths: Record<string, JSX.Element> = {
  play: <path d="M6 4l14 8-14 8z" />,
  webhook: (
    <>
      <path d="M12 3a4 4 0 0 1 4 4c0 1.5-.8 2.8-2 3.5L12 14" />
      <path d="M5 17a4 4 0 0 0 7 2.6L12 14M19 17a4 4 0 0 1-7 2.6" />
      <circle cx="12" cy="14" r="1.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  plug: <path d="M9 2v6M15 2v6M6 8h12v3a6 6 0 0 1-12 0zM12 17v5" />,
  bolt: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  dedupe: (
    <>
      <rect x="3" y="3" width="10" height="10" rx="2" />
      <rect x="11" y="11" width="10" height="10" rx="2" />
      <path d="M6 8l1.5 1.5L10 6" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  table: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M3 10h18M9 4v16" />
    </>
  ),
  chat: <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9l-5 4z" />,
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
    </>
  ),
  brain: (
    <>
      <path d="M9 4a3 3 0 0 0-3 3v1a3 3 0 0 0-2 3 3 3 0 0 0 2 3v1a3 3 0 0 0 3 3h1V4z" />
      <path d="M15 4a3 3 0 0 1 3 3v1a3 3 0 0 1 2 3 3 3 0 0 1-2 3v1a3 3 0 0 1-3 3h-1V4z" />
    </>
  ),
  terminal: <path d="M4 17l6-5-6-5M12 19h8" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>
  ),
  branch: <path d="M6 4v16M6 8c0 4 12 2 12 8M18 4v2M18 20v-2" />,
  switch: <path d="M4 6h5l6 12h5M4 18h5l2-4M20 6l-3-3M20 6l-3 3M20 18l-3-3M20 18l-3 3" />,
  loop: <path d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 3v4h-4M6 21v-4h4" />,
  repeat: <path d="M17 2l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3" />,
  merge: <path d="M6 4v5a4 4 0 0 0 4 4h4a4 4 0 0 1 4 4v3M18 4v5a4 4 0 0 1-4 4" />,
  hourglass: <path d="M6 2h12M6 22h12M7 2v4a5 5 0 0 0 10 0V2M7 22v-4a5 5 0 0 1 10 0v4" />,
  stop: <rect x="5" y="5" width="14" height="14" rx="2" />,
  function: <path d="M14 4h-2a2 2 0 0 0-2 2v4H7v2h3v6a2 2 0 0 1-2 2H6M14 12l4 4M18 12l-4 4" />,
  edit: <path d="M4 20h4l10-10-4-4L4 16zM13 7l4 4" />,
  filter: <path d="M4 5h16l-6 7v6l-4 2v-8z" />,
  split: <path d="M12 3v8M12 11l-6 6M12 11l6 6M4 19h4M16 19h4" />,
  collect: <path d="M6 5l6 6 6-6M12 11v10M4 21h16" />,
  sort: <path d="M4 7h10M4 12h7M4 17h4M18 5v14M15 16l3 3 3-3" />,
  limit: <path d="M4 6h16M4 12h10M4 18h5" />,
  code: <path d="M8 8l-4 4 4 4M16 8l4 4-4 4M14 4l-4 16" />,
  reply: <path d="M9 14L4 9l5-5M4 9h9a7 7 0 0 1 7 7v4" />,
  note: <path d="M6 3h9l5 5v13H6zM15 3v5h5M9 13h6M9 17h6" />,
  workflow: (
    <>
      <rect x="3" y="4" width="6" height="6" rx="1" />
      <rect x="15" y="14" width="6" height="6" rx="1" />
      <path d="M9 7h4a2 2 0 0 1 2 2v5" />
    </>
  ),
}

export function NodeIcon({ name, ...rest }: { name: string } & SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...rest}>
      {paths[name] ?? paths.workflow}
    </svg>
  )
}
